import { Trans, useLingui } from '@lingui/react';
import { Accordion } from '@startuptools/ui/Accordion';
import { AccordionDetails } from '@startuptools/ui/AccordionDetails';
import { AccordionGroup } from '@startuptools/ui/AccordionGroup';
import { AccordionSummary } from '@startuptools/ui/AccordionSummary';
import { Alert } from '@startuptools/ui/Alert';
import { Button } from '@startuptools/ui/Button';
import { Checkbox } from '@startuptools/ui/Checkbox';
import { DialogActions, DialogContent } from '@startuptools/ui/Dialog';
import { FormControl } from '@startuptools/ui/FormControl';
import { FormLabel } from '@startuptools/ui/FormLabel';
import { Input } from '@startuptools/ui/Input';
import { Typography } from '@startuptools/ui/Typography';
import React from 'react';
import { Controller, useForm } from 'react-hook-form';
import { type GqlWarrantProgramListViewQuery } from '../common/warrant-programs.graphql';

type FormData = Pick<GqlWarrantProgramListViewQuery['warrantPrograms']['models'][0], 'description' | 'warrantsForQeso'>;

export const CreateWarrantProgramForm = ({
  onClose,
  onSave,
  type,
  error,
  loading,
}: {
  onClose: () => void;
  onSave: (data: FormData) => Promise<void>;
  type: 'wp' | 'bond' | 'qeso';
  error?: React.ReactNode;
  loading: boolean;
}) => {
  const { control, handleSubmit } = useForm<FormData>({ defaultValues: { warrantsForQeso: false, description: '' } });
  const { i18n } = useLingui();
  return (
    <form onSubmit={handleSubmit(onSave)}>
      <DialogContent>
        <Controller
          control={control}
          name="description"
          rules={{ required: true }}
          render={({ field, formState }) => (
            <FormControl error={Boolean(formState.errors?.description)}>
              <FormLabel>
                <Trans id="Description" />
              </FormLabel>
              <Input {...field} placeholder={i18n._('(Eg. Options program for Alice, Bob or Eve)')} />
            </FormControl>
          )}
        />

        {type === 'wp' && (
          <AccordionGroup variant="soft">
            <Accordion>
              <AccordionSummary>
                <Typography level="title-md">
                  <Trans id="Advanced settings" />
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <FormControl>
                  <Controller
                    name="warrantsForQeso"
                    control={control}
                    render={({ field: { value, ...props } }) => (
                      <Checkbox
                        {...props}
                        checked={value}
                        onChange={e => {
                          return props.onChange(e.target.checked);
                        }}
                        label={
                          <Typography fontSize="body-sm">
                            <Trans id="The warrants shall be tied to qualified employee stock options" />
                          </Typography>
                        }
                      />
                    )}
                  />
                </FormControl>
              </AccordionDetails>
            </Accordion>
          </AccordionGroup>
        )}
        {error && <Alert color="danger">{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={onClose}>
          <Trans id="Cancel" />
        </Button>
        <Button type="submit" loading={loading}>
          <Trans id="Continue" />
        </Button>
      </DialogActions>
    </form>
  );
};
